import { useCallback, useEffect, useRef, useState } from 'react';
import { useAtomValue, useSetAtom } from 'jotai';
import { STAGE_EVENTS } from '../../../labelling/providers/core-stage-provider/constants';
import { useCoreStageContext } from '../../../labelling/providers/core-stage-provider/context';
import { hoveredNodeIdAtom, selectedNodeIdAtom } from '../../atoms/skeleton-template-builder/template-atoms';
import { templateDispatchAtom } from '../../atoms/skeleton-template-builder/template-dispatch';
import type { EdgePreviewT, PendingNodeT } from '../../types/skeleton-template-builder.type';
import { EVENTS, UNDOABLE_EVENTS } from '../../types/skeleton-template-builder.type';
import type { DragModeT } from './constants';
import { DRAG_MODE } from './constants';
import { generateNodeId, getNodeIdFromTarget, getStageEventDetail } from './utils';
type TemplateCanvasInteractionsT = {
  pendingNode: PendingNodeT | null;
  edgePreview: EdgePreviewT | null;
  onConfirmNode: (label: string, color: string) => void;
  onCancelNode: () => void;
};
export function useTemplateCanvasInteractions(): TemplateCanvasInteractionsT {
  const {
    stage
  } = useCoreStageContext();
  const dispatch = useSetAtom(templateDispatchAtom);
  const selectedNodeId = useAtomValue(selectedNodeIdAtom);
  const hoveredNodeId = useAtomValue(hoveredNodeIdAtom);
  const [pendingNode, setPendingNode] = useState<PendingNodeT | null>(null);
  const [edgePreview, setEdgePreview] = useState<EdgePreviewT | null>(null);
  const dragRef = useRef<{
    mode: DragModeT | null;
    draggedNodeId: string | null;
  }>({
    mode: null,
    draggedNodeId: null
  });
  const onConfirmNode = useCallback((label: string, color: string) => {
    if (!pendingNode) return;
    dispatch({
      type: UNDOABLE_EVENTS.ADD_NODE,
      node: {
        id: pendingNode.id,
        label,
        color,
        x: pendingNode.x,
        y: pendingNode.y
      }
    });
    setPendingNode(null);
  }, [dispatch, pendingNode]);
  const onCancelNode = useCallback(() => setPendingNode(null), []);
  const onPointerUp = useCallback((event: Event) => {
    if (pendingNode) return;
    const {
      mousePos
    } = getStageEventDetail(event);
    setPendingNode({
      id: generateNodeId(),
      x: mousePos[0],
      y: mousePos[1]
    });
  }, [pendingNode]);
  const onDragStart = useCallback((event: Event) => {
    const {
      mousePos,
      dragTarget
    } = getStageEventDetail(event);
    const nodeId = getNodeIdFromTarget(dragTarget);
    if (!nodeId) return;
    dragRef.current.draggedNodeId = nodeId;

    // Dragging the already-selected node draws an edge; dragging any other node moves it
    if (selectedNodeId === nodeId) {
      dragRef.current.mode = DRAG_MODE.DRAW_EDGE;
      setEdgePreview({
        sourceNodeId: nodeId,
        xPx: mousePos[0],
        yPx: mousePos[1]
      });
    } else {
      dragRef.current.mode = DRAG_MODE.MOVE_NODE;
    }
  }, [selectedNodeId]);
  const onDragMove = useCallback((event: Event) => {
    const {
      mousePos
    } = getStageEventDetail(event);
    const {
      mode,
      draggedNodeId
    } = dragRef.current;
    if (mode === DRAG_MODE.DRAW_EDGE) {
      setEdgePreview(prev => prev ? {
        ...prev,
        xPx: mousePos[0],
        yPx: mousePos[1]
      } : null);
    } else if (mode === DRAG_MODE.MOVE_NODE && draggedNodeId) {
      dispatch({
        type: EVENTS.MOVE_NODE,
        nodeId: draggedNodeId,
        x: mousePos[0],
        y: mousePos[1]
      });
    }
  }, [dispatch]);
  const onDragEnd = useCallback(() => {
    if (dragRef.current.mode === DRAG_MODE.DRAW_EDGE) {
      const sourceNodeId = dragRef.current.draggedNodeId;
      if (hoveredNodeId && sourceNodeId && hoveredNodeId !== sourceNodeId) {
        dispatch({
          type: UNDOABLE_EVENTS.ADD_EDGE,
          edge: {
            sourceNodeId,
            targetNodeId: hoveredNodeId
          }
        });
      }
      setEdgePreview(null);
    }
    dragRef.current = {
      mode: null,
      draggedNodeId: null
    };
  }, [dispatch, hoveredNodeId]);
  useEffect(() => {
    stage.addEventListener(STAGE_EVENTS.POINTER_UP, onPointerUp);
    stage.addEventListener(STAGE_EVENTS.DRAG_START, onDragStart);
    stage.addEventListener(STAGE_EVENTS.DRAG, onDragMove);
    stage.addEventListener(STAGE_EVENTS.DRAG_END, onDragEnd);
    return () => {
      stage.removeEventListener(STAGE_EVENTS.POINTER_UP, onPointerUp);
      stage.removeEventListener(STAGE_EVENTS.DRAG_START, onDragStart);
      stage.removeEventListener(STAGE_EVENTS.DRAG, onDragMove);
      stage.removeEventListener(STAGE_EVENTS.DRAG_END, onDragEnd);
    };
  }, [stage, onPointerUp, onDragStart, onDragMove, onDragEnd]);
  return {
    pendingNode,
    edgePreview,
    onConfirmNode,
    onCancelNode
  };
}